import DataLoader from 'dataloader'
import { type IamApi } from '@/features/iam/api/iam-api'
import { User } from './User'
import { collection } from '@/infra/collection'
import { mapUserResourceToProps } from './user-mappers'
import type { PrimeUserProps } from '@/features/iam'
import { Id } from '@/infra/id'
import { defer, type Observable, of } from 'rxjs'
import { runInAction } from 'mobx'
import { distinctUntilChanged } from 'rxjs/operators'
import type { OnlineStatusForUserFn } from '@/features/messaging'

/**
 * A request to reset a user's password.
 */
export interface PasswordResetRequest {
  id: string
  email: string
}

export class UserStore {
  /**
   * Data loader for users.
   *
   * @private
   */
  private readonly _loader = new DataLoader(this._fetchManyInternal.bind(this))

  /**
   * Users.
   */
  readonly users = collection({
    create: () => {
      // Deferred because the user's IDs are only set after it is created.
      const user: User = new User(
        this._isCurrentUser,
        defer(() => this._onlineStatus$(user)),
      )
      return user
    },
    getDataId: (data) => data.publicId,
    getModelId: (model) => model.publicId,
  })

  constructor(
    private readonly _api: IamApi,
    private readonly _isCurrentUser: (user: User) => boolean,
    private readonly _onlineStatusForUser: OnlineStatusForUserFn,
  ) {}

  /**
   * Fetches a password reset request. Works without being signed in.
   */
  async fetchPasswordResetRequest(
    id: string,
    signal?: AbortSignal,
  ): Promise<PasswordResetRequest> {
    const resource = await this._api.users.getPasswordResetRequest(id, signal)
    return { id: resource.id, email: resource.email }
  }

  /**
   * Resets a password through a password reset request. Works without being
   * signed in.
   */
  resetPassword(passwordResetRequestId: string, password: string) {
    return this._api.users.resetPassword(passwordResetRequestId, password)
  }

  /**
   * Fetches a user using the given token instead of the session's.
   */
  async fetchUserUsingToken(
    publicId: string,
    token: string,
    signal?: AbortSignal,
  ) {
    const resource = await this._api.users.get(publicId, token, signal)
    const user = this.users.set(mapUserResourceToProps(resource))
    this._loader.prime(resource.public_id, user)
    return user
  }

  /**
   * Fetches a user by public ID, batched and cached.
   */
  async fetchUser(id: Id) {
    return this._loader.load(Id.assert(id))
  }

  primeUser(props: PrimeUserProps) {
    if (!props.publicId) {
      return undefined
    }

    const user = this.users.set({
      publicId: props.publicId,
      ...(props.id && {
        id: props.id,
      }),
    })

    void this.fetchUser(props.publicId)

    if (user.isFullyLoaded) {
      // The user has already been hydrated from the server, so don't
      // assign any props to it
      return user
    }

    return user.set(props)
  }

  /**
   * Batching implementation for getting users.
   *
   * @private
   * @param userPublicIds
   */
  private async _fetchManyInternal(
    userPublicIds: readonly string[],
  ): Promise<(User | null)[]> {
    const response = await this._api.users.getManyByPublicId(userPublicIds)
    // Mark loaded users.
    return runInAction(() => {
      const users = this.users.set(
        response.map((r) => (r ? mapUserResourceToProps(r) : r)),
      )
      users.forEach((user) => {
        if (user) {
          user.markFullyLoaded()
        }
      })
      return users
    })
  }

  private _onlineStatus$(user: User): Observable<boolean> {
    // The current user is always online.
    return (
      user.isCurrentUser ? of(true) : this._onlineStatusForUser(user.publicId)
    ).pipe(distinctUntilChanged())
  }
}
